import useTranslation from "@hooks/use_translation";
import ApplicationCardList from "./application_card_list";
import DreamJobCallout from "./dream_job_callout";
import { Stack } from "@sprout/layout";
import type Application from "../../types/application";
import { TextInput } from "@sprout/text_input.tsx";
import { Trans } from "react-i18next";
import SearchIcon from "@/sprout/icons/search";
import { Tabs, TabsList, TabsTrigger } from "@/sprout/tabs";
import ApplicationSortDropdown from "@components/applications/application_sort_dropdown.tsx";

type Props = {
  activeApplications: Application[];
  inactiveApplications?: Application[];
  onApplicationSelect: (id: number) => void;
  isLoading: boolean;
  selectedApplication?: Application | undefined;
  hasTokens: boolean;
  tab: string;
  setTab: (tab: string) => void;
  search: string;
  setSearch: (search: string) => void;
  setSortBy: (sortBy: string) => void;
  totalActiveCount: number;
  totalInactiveCount: number;
  activeHasNextPage: boolean;
  activeIsFetchingNextPage: boolean;
  onLoadMoreActive: () => void;
  inactiveHasNextPage: boolean;
  inactiveIsFetchingNextPage: boolean;
  onLoadMoreInactive: () => void;
};

const ApplicationsList = (props: Props) => {
  const {
    activeApplications,
    inactiveApplications,
    onApplicationSelect,
    isLoading,
    selectedApplication,
    hasTokens,
    tab,
    setTab,
    search,
    setSearch,
    setSortBy,
    totalActiveCount,
    totalInactiveCount,
    activeHasNextPage,
    activeIsFetchingNextPage,
    onLoadMoreActive,
    inactiveHasNextPage,
    inactiveIsFetchingNextPage,
    onLoadMoreInactive,
  } = props;
  const { t } = useTranslation("applications");

  const totalApplications = totalActiveCount + totalInactiveCount;

  const sortOptions = [
    { label: t("sort_options.updated"), value: "updated" },
    { label: t("sort_options.dream_job"), value: "dream_job" },
    { label: t("sort_options.newest"), value: "newest" },
    { label: t("sort_options.oldest"), value: "oldest" },
  ];

  return (
    <div className="applications-list h-full flex flex-col">
      <Stack gap="8px" className="shrink-0 applications-list__header">
        {!isLoading && (
          <div className="applications-list-header">
            <TextInput
              id="search"
              placeholder={t("search")}
              onChange={(event) => setSearch(event.currentTarget.value || "")}
              maxLength={255}
              value={search}
              narrow={true}
              icon={<SearchIcon />}
              onClear={() => setSearch("")}
            />
            <ApplicationSortDropdown
              label={t("sort")}
              options={sortOptions}
              onSelect={(v) => setSortBy(v)}
            />
          </div>
        )}
        {!isLoading && (
          <Tabs defaultValue={tab} onValueChange={setTab} className="w-full">
            <TabsList>
              <TabsTrigger value="active">
                {t("active_with_count", { count: totalActiveCount })}
              </TabsTrigger>
              <TabsTrigger value="inactive">
                {t("inactive_with_count", { count: totalInactiveCount })}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </Stack>
      {!isLoading && (
        <div className="applications-list__scroll-area overflow-auto min-h-0 flex-1">
          {tab === "active" && hasTokens && (
            <div className="sticky top-0 z-10 bg-white pt-2">
              <DreamJobCallout />
            </div>
          )}
          {totalApplications === 0 && (
            <p className="empty-message">
              <Trans i18nKey="applications:empty_message" />
            </p>
          )}
          {tab === "active" && activeApplications && (
            <div className="mt-2">
              <ApplicationCardList
                selectedApplicationId={selectedApplication?.id}
                applications={activeApplications}
                onApplicationSelect={onApplicationSelect}
                hasNextPage={activeHasNextPage}
                isFetchingNextPage={activeIsFetchingNextPage}
                onLoadMore={onLoadMoreActive}
              />
            </div>
          )}
          {tab === "inactive" && inactiveApplications && (
            <div className="mt-2">
              <ApplicationCardList
                selectedApplicationId={selectedApplication?.id}
                applications={inactiveApplications}
                onApplicationSelect={onApplicationSelect}
                hasNextPage={inactiveHasNextPage}
                isFetchingNextPage={inactiveIsFetchingNextPage}
                onLoadMore={onLoadMoreInactive}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationsList;
